// One batch's report — the table three roles read and one of them writes to.
//
// The developer marks fixes here, the tester watches the outcome, and the PM
// checks whether the loop is closing. Building it once is not just tidiness:
// these three views have to agree on what "failed" and "dev says passed" mean,
// or the re-run logic and the human reading it drift apart.
//
// Everything pages and filters on the SERVER. The version this replaces rendered
// a raw <table> of every case in the batch with a client-side text filter and no
// pagination at all, which fell over on a large suite.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, CheckCircle2, XCircle, Clock, MessageSquare,
} from "lucide-react";
import DataTable from "@/components/dashboard/DataTable";
import CaseDetailModal from "@/components/shared/CaseDetailModal";
import Modal from "@/components/dashboard/Modal";
import Button from "@/components/ui/Button";
import { Badge, StatusBadge } from "@/components/ui/Badge";
import { setDevStatus, setDevStatusBulk, DEV_STATUS_OPTIONS } from "@/api/developer";
import { cn } from "@/lib/cn";

const PAGE_SIZE = 25;
// The server truncates the developer's note at 2000 chars. Say so in the UI rather
// than silently swallowing the tail of a root-cause write-up.
const NOTE_MAX = 2000;

// A case can only be judged once it has actually finished running.
const SETTLED = ["passed", "failed", "error", "blocked", "cancelled", "orphaned", "inconclusive"];

// The order statuses are offered in, worst-first — a developer opens this table to
// find what needs fixing, so "failed" should not be buried under "cancelled".
// Anything the server reports that isn't listed here still shows, after these.
const STATUS_ORDER = [
  "failed", "error", "blocked", "inconclusive", "passed",
  "cancelled", "orphaned", "queued", "requeued", "claimed", "running",
];
const DEV_STATUS_ORDER = ["failed", "blocked", "passed"];

const STATUS_LABEL = {
  passed: "Passed", failed: "Failed", error: "Error", blocked: "Blocked",
  cancelled: "Cancelled", orphaned: "Orphaned", inconclusive: "Inconclusive",
  queued: "Queued", requeued: "Requeued", claimed: "Claimed", running: "Running",
};
const titleCase = (v) => STATUS_LABEL[v] || String(v).replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

/**
 * Turn the server's whole-batch tally into filter options.
 *
 * Only statuses this sheet ACTUALLY contains are offered, each labelled with its
 * real count ("Failed 43"). Two things this deliberately does not do:
 *   - derive the options from `rows`, which is one page of 25 — you could then
 *     only filter to a status that happened to be on the page you were looking
 *     at, and picking it collapsed the list to that page;
 *   - offer the full fixed vocabulary, which put eleven chips on screen for a
 *     batch that only ever produced two of them, nine of which returned nothing.
 * `keep` preserves the currently-selected value even at zero, so the chip you
 * picked cannot vanish from under you the moment it matches nothing.
 */
function buildOptions(tally, order, keep) {
  const counts = tally || {};
  const present = new Set(Object.keys(counts).filter((k) => counts[k] > 0));
  if (keep && keep !== "all") present.add(keep);
  const ranked = [...present].sort((a, b) => {
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
  });
  return ranked.map((value) => ({ value, label: titleCase(value), count: counts[value] || 0 }));
}

const DEV_TONE = { passed: "emerald", failed: "rose", blocked: "amber" };

// How the developer's claim currently stands — mirrors dev_verify_state on the
// backend. "Awaiting re-run" is the important one: the dev says it's fixed, and
// nothing has verified that yet.
const VERIFY = {
  awaiting_rerun: { label: "Awaiting re-run", tone: "sky", Icon: Clock },
  verified: { label: "Verified", tone: "emerald", Icon: CheckCircle2 },
  still_failing: { label: "Still failing", tone: "rose", Icon: XCircle },
  recorded: { label: "Recorded", tone: "slate", Icon: MessageSquare },
};

/**
 * @param {string}   kind      "exe" | "extension"
 * @param {string}   batchId
 * @param {function} fetchPage (params) => {items, total} — how this role reads it
 * @param {boolean}  canJudge  show the developer's verdict controls
 * @param {function} onChanged called after a verdict is written
 */
export default function BatchReportTable({
  kind, batchId, fetchPage, canJudge = false, onChanged, projectId,
}) {
  const [page, setPage] = useState(1);
  const [criteria, setCriteria] = useState({ query: "", filters: {} });
  const [data, setData] = useState({ items: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openCase, setOpenCase] = useState(null);
  const [judging, setJudging] = useState(null);       // the row being marked
  // Selection for bulk marking. Ids, not rows, so a refetch (or paging away and
  // back) keeps the selection intact instead of losing whatever left the DOM.
  const [selected, setSelected] = useState(() => new Set());
  // "apply to every case the filter matched", not just the page on screen —
  // the report pages server-side, so ticking the header box reaches 25 of 166.
  const [applyToAll, setApplyToAll] = useState(false);
  const [bulk, setBulk] = useState(false);            // bulk dialog open
  const [bulkResult, setBulkResult] = useState(null);
  const [verdict, setVerdict] = useState({ status: "passed", note: "" });
  const [saving, setSaving] = useState(false);
  // Kept separate from `error`: that one is the load banner and every refetch
  // clears it, which would wipe a save failure the instant the table reloaded.
  const [saveError, setSaveError] = useState(null);
  // Only the newest request may write state. The search box fires one request per
  // debounce tick, and a slow early page landing after a fast later one left the
  // table showing results for a query nobody had asked for.
  const reqSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true); setError(null);
    try {
      const res = await fetchPage({
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        search: criteria.query || undefined,
        status: criteria.filters.status !== "all" ? criteria.filters.status : undefined,
        dev_status: criteria.filters.dev_status !== "all"
          ? criteria.filters.dev_status : undefined,
      });
      if (seq !== reqSeq.current) return;
      setData({
        items: res.items || [],
        total: res.total ?? (res.items || []).length,
        // Whole-batch tallies, unaffected by the current filter — that is what
        // makes "Failed 43" mean 43 in this run rather than 43 on this page.
        counts: res.counts || {},
      });
    } catch (e) {
      if (seq !== reqSeq.current) return;
      setError((e && e.message) || "Could not load this report.");
      // Keep the tallies: they describe the batch, not the failed request, and
      // wiping them would empty the filter bar the reader is mid-way through.
      setData((prev) => ({ items: [], total: 0, counts: prev.counts }));
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [fetchPage, page, criteria]);

  useEffect(() => { load(); }, [load]);

  // A selection only means something against the result set it was made in.
  // Changing the filter or the search silently re-points "all matching" at a
  // different set of cases, so both are dropped rather than carried over.
  useEffect(() => {
    setSelected(new Set());
    setApplyToAll(false);
  }, [criteria]);

  function openVerdict(row) {
    setJudging(row);
    setSaveError(null);
    setVerdict({ status: row.dev_status || "passed", note: row.dev_note || "" });
  }

  async function saveVerdict(clear = false) {
    if (!judging || saving) return;      // a double-click must not fire two writes
    setSaving(true);
    setSaveError(null);
    try {
      await setDevStatus(kind, batchId, judging.id, {
        devStatus: clear ? null : verdict.status,
        note: clear ? null : (verdict.note.trim() || null),
      });
      setJudging(null);
      await load();
      onChanged?.();
    } catch (e) {
      // Rendered INSIDE the dialog. The page-level banner sits behind the modal
      // scrim, so a 409 "that case is still running" was invisible and Save simply
      // appeared to do nothing at all.
      setSaveError((e && e.message) || "Could not save that verdict.");
    } finally {
      setSaving(false);
    }
  }

  async function saveBulk(clear = false) {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await setDevStatusBulk(kind, batchId, {
        devStatus: clear ? null : verdict.status,
        note: clear ? null : (verdict.note.trim() || null),
        itemIds: applyToAll ? [] : [...selected],
        allMatching: applyToAll,
        // Sent so the server resolves the SAME set the reader is looking at.
        status: criteria.filters.status !== "all" ? criteria.filters.status : null,
        devStatusFilter: criteria.filters.dev_status !== "all"
          ? criteria.filters.dev_status : null,
        search: criteria.query || null,
      });
      setBulk(false);
      setSelected(new Set());
      setApplyToAll(false);
      // Reported, not assumed: cases still running are skipped, and a developer
      // who marked 25 and moved 24 needs to be told which.
      setBulkResult(res);
      await load();
      onChanged?.();
    } catch (e) {
      setSaveError((e && e.message) || "Could not save those verdicts.");
    } finally {
      setSaving(false);
    }
  }

  const rows = useMemo(() => (data.items || []).map((i) => ({
    ...i,
    settled: SETTLED.includes(String(i.status || "").toLowerCase()),
  })), [data.items]);

  // Built from the whole-batch tally, so the bar shows exactly the results this
  // sheet contains and what each one is worth. `total` labels the "All" chip.
  const filters = useMemo(() => {
    const statusOpts = buildOptions(data.counts?.by_status, STATUS_ORDER, criteria.filters.status);
    const devOpts = buildOptions(data.counts?.by_dev_status, DEV_STATUS_ORDER, criteria.filters.dev_status);
    const out = [];
    if (statusOpts.length > 1 || criteria.filters.status) {
      out.push({ key: "status", label: "results", total: data.counts?.total, options: statusOpts });
    }
    // Nobody has recorded a verdict yet → the whole facet is noise. It appears
    // as soon as there is something to filter by.
    if (devOpts.length) {
      out.push({
        key: "dev_status", label: "dev verdicts",
        total: data.counts?.dev_marked, options: devOpts, as: "select",
      });
    }
    return out;
  }, [data.counts, criteria.filters.status, criteria.filters.dev_status]);

  const columns = [
    {
      key: "name", header: "Test case",
      render: (r) => (
        <div className="min-w-0 max-w-md">
          <p className="line-clamp-2 text-slate-700 dark:text-slate-200">
            {r.name || r.instruction}
          </p>
          {r.url && <p className="truncate text-xs text-slate-400">{r.url}</p>}
        </div>
      ),
    },
    {
      // NOT sortable: rows come from the server ordered by order_index and neither
      // runner's item query takes a sort param, so a sortable header only flipped a
      // chevron and silently bounced the reader back to page 1.
      key: "status", header: "Agent result",
      render: (r) => <StatusBadge status={r.status} />,
    },
    {
      key: "dev_status", header: "Developer says",
      render: (r) => (
        r.dev_status ? (
          <Badge tone={DEV_TONE[r.dev_status] || "slate"} className="capitalize">
            {r.dev_status}
          </Badge>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )
      ),
    },
    {
      key: "dev_verify_state", header: "Verification",
      render: (r) => {
        const v = VERIFY[r.dev_verify_state];
        if (!v) return <span className="text-xs text-slate-400">—</span>;
        return <Badge tone={v.tone}><v.Icon size={11} /> {v.label}</Badge>;
      },
    },
    {
      // Both runners serialize this as `attempt` (singular). Reading `attempts`
      // made the column a hardcoded 1 on every row of every batch, so a
      // first-attempt failure looked identical to one that burned all its retries.
      key: "attempt", header: "Tries", align: "center",
      render: (r) => <span className="text-slate-500">{r.attempt || 1}</span>,
    },
    ...(canJudge ? [{
      key: "actions", header: "", align: "right",
      render: (r) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="outline" size="sm"
            onClick={() => openVerdict(r)}
            disabled={!r.settled}
            title={r.settled
              ? "Record what you found"
              : "This case is still running — wait for it to finish"}
          >
            {r.dev_status ? "Update" : "Mark"}
          </Button>
        </div>
      ),
    }] : []),
  ];

  return (
    <>
      {error && (
        <div className="mb-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
          {error}
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        searchKeys={["name", "url"]}
        filters={filters}
        pageSize={PAGE_SIZE}
        maxHeight="34rem"
        onRowClick={setOpenCase}
        // Only a developer who may judge gets checkboxes — for everyone else the
        // column would be a control that does nothing.
        selectable={canJudge}
        selectedIds={selected}
        onSelectionChange={(next) => {
          setSelected(next);
          // Un-ticking anything means the reader no longer means "all of them".
          if (next.size === 0) setApplyToAll(false);
        }}
        toolbarExtra={canJudge && selected.size > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
              {applyToAll
                ? `All ${data.total} matching cases selected`
                : `${selected.size} selected`}
            </span>
            {/* The page-vs-result-set distinction, made explicit. Without this a
                developer filters to Failed, ticks the header box and believes
                they marked all 166 when they marked 25. */}
            {!applyToAll && data.total > rows.length && selected.size === rows.length && (
              <button
                type="button"
                onClick={() => setApplyToAll(true)}
                className="text-sm font-medium text-brand-600 underline-offset-2 hover:underline dark:text-brand-300"
              >
                Select all {data.total} matching
              </button>
            )}
            {applyToAll && (
              <button
                type="button"
                onClick={() => setApplyToAll(false)}
                className="text-sm font-medium text-slate-500 underline-offset-2 hover:underline"
              >
                Just this page
              </button>
            )}
            <Button size="sm" onClick={() => {
              setSaveError(null);
              setVerdict({ status: "passed", note: "" });
              setBulk(true);
            }}>
              Mark {applyToAll ? data.total : selected.size} case{(applyToAll ? data.total : selected.size) === 1 ? "" : "s"}
            </Button>
          </div>
        ) : null}
        emptyLabel={loading ? "Loading…" : "No cases match those filters."}
        server={{
          total: data.total,
          page,
          loading,
          onChange: ({ page: p, query, filters }) => {
            setPage(p);
            setCriteria((prev) =>
              prev.query === query && JSON.stringify(prev.filters) === JSON.stringify(filters)
                ? prev
                : { query, filters },
            );
          },
        }}
      />

      <CaseDetailModal
        testCase={openCase}
        kind={kind}
        // Without this the modal never fetches the generated case, so a report
        // opened from here showed no steps, no test data and no expected
        // result — the fields a developer needs most to judge a failure.
        projectId={projectId}
        onClose={() => setOpenCase(null)}
        actions={
          canJudge && openCase?.settled ? (
            <Button
              variant="outline" size="sm"
              onClick={() => { openVerdict(openCase); setOpenCase(null); }}
            >
              {openCase.dev_status ? "Update verdict" : "Mark this case"}
            </Button>
          ) : null
        }
      />

      {/* bulk verdict ------------------------------------------------------ */}
      <Modal
        open={bulk}
        onClose={() => { if (!saving) setBulk(false); }}
        title={`Mark ${applyToAll ? data.total : selected.size} cases`}
        footer={
          <div className="flex items-center justify-between gap-3">
            <Button variant="outline" size="sm" onClick={() => saveBulk(true)} disabled={saving}>
              Clear verdicts
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setBulk(false)} disabled={saving}>
                Cancel
              </Button>
              <Button size="sm" onClick={() => saveBulk(false)} disabled={saving}>
                {saving ? <Loader2 size={15} className="animate-spin" /> : "Apply to all"}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:bg-white/5 dark:text-slate-200">
            The same verdict and note go on{" "}
            <b>{applyToAll ? `all ${data.total} cases matching the current filter` : `${selected.size} selected case${selected.size === 1 ? "" : "s"}`}</b>.
            Cases still running are skipped.
          </p>

          {saveError && (
            <p className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
              {saveError}
            </p>
          )}

          <div className="space-y-2" role="radiogroup" aria-label="Verdict for every selected case">
            {DEV_STATUS_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={verdict.status === o.value}
                onClick={() => setVerdict((v) => ({ ...v, status: o.value }))}
                className={cn(
                  "flex w-full flex-col rounded-xl border px-4 py-3 text-left transition-colors",
                  verdict.status === o.value
                    ? "border-brand-400 bg-brand-50/70 dark:border-brand-500/40 dark:bg-brand-500/10"
                    : "border-slate-200 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5",
                )}
              >
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{o.label}</span>
                <span className="text-xs text-slate-500 dark:text-slate-400">{o.hint}</span>
              </button>
            ))}
          </div>

          <div>
            <label htmlFor="bulk-verdict-note" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Note (optional) — applied to every one
            </label>
            <textarea
              id="bulk-verdict-note"
              rows={3}
              value={verdict.note}
              onChange={(e) => setVerdict((v) => ({ ...v, note: e.target.value }))}
              placeholder="e.g. Fixed in release 2.4 — please re-run"
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
            />
          </div>
        </div>
      </Modal>

      {/* what the bulk write actually did ---------------------------------- */}
      <Modal
        open={!!bulkResult}
        onClose={() => setBulkResult(null)}
        title="Done"
        footer={
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setBulkResult(null)}>Close</Button>
          </div>
        }
      >
        <p className="text-sm text-slate-700 dark:text-slate-200">
          Marked <b>{bulkResult?.updated ?? 0}</b> case
          {(bulkResult?.updated ?? 0) === 1 ? "" : "s"}.
        </p>
        {/* Said out loud rather than folded into the count: a developer who asked
            for 25 and got 24 needs to know which one didn't take, and why. */}
        {bulkResult?.skipped_running > 0 && (
          <p className="mt-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
            {bulkResult.skipped_running} case{bulkResult.skipped_running === 1 ? " was" : "s were"} still
            running and left alone — mark those once they finish.
          </p>
        )}
      </Modal>

      {/* developer verdict ------------------------------------------------- */}
      <Modal
        open={!!judging}
        // Ignore Escape / scrim clicks while the write is in flight, or the dialog
        // vanishes mid-request and the developer never learns whether it landed.
        onClose={() => { if (!saving) setJudging(null); }}
        title="What did you find?"
        footer={
          <div className="flex items-center justify-between gap-3">
            {judging?.dev_status ? (
              <Button variant="outline" size="sm" onClick={() => saveVerdict(true)} disabled={saving}>
                Clear verdict
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setJudging(null)} disabled={saving}>Cancel</Button>
              <Button size="sm" onClick={() => saveVerdict(false)} disabled={saving}>
                {saving ? <Loader2 size={15} className="animate-spin" /> : "Save"}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:bg-white/5 dark:text-slate-200">
            {judging?.name || judging?.instruction}
          </p>

          {saveError && (
            <p className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
              {saveError}
            </p>
          )}

          {/* A real radio group: the selected verdict was previously conveyed by
              border colour alone, so a screen reader announced three plain buttons
              with no hint which one Save was about to write. */}
          <div className="space-y-2" role="radiogroup" aria-label="Your verdict on this case">
            {DEV_STATUS_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={verdict.status === o.value}
                onClick={() => setVerdict((v) => ({ ...v, status: o.value }))}
                className={cn(
                  "flex w-full flex-col rounded-xl border px-4 py-3 text-left transition-colors",
                  verdict.status === o.value
                    ? "border-brand-400 bg-brand-50/70 dark:border-brand-500/40 dark:bg-brand-500/10"
                    : "border-slate-200 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5",
                )}
              >
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {o.label}
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400">{o.hint}</span>
              </button>
            ))}
          </div>

          <div>
            <label htmlFor="dev-verdict-note" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
              Note (optional)
            </label>
            <textarea
              id="dev-verdict-note"
              rows={3}
              maxLength={NOTE_MAX}
              value={verdict.note}
              onChange={(e) => setVerdict((v) => ({ ...v, note: e.target.value }))}
              placeholder="What was wrong, and what did you change?"
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-400 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white"
            />
            <div className="mt-1.5 flex items-start justify-between gap-3">
              <p className="text-xs text-slate-400">
                The tester sees this, and your claim is checked automatically on their
                next re-run — it never overwrites what the agent found.
              </p>
              {/* The server cuts the note at NOTE_MAX. Without a visible budget a
                  4,000-character write-up saved "successfully" and came back
                  ending mid-word. */}
              <span className={cn(
                "shrink-0 text-xs tabular-nums",
                verdict.note.length >= NOTE_MAX ? "font-medium text-amber-600 dark:text-amber-400" : "text-slate-400",
              )}>
                {verdict.note.length}/{NOTE_MAX}
              </span>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
